import React from 'react';
import {
  Alert,
  Box,
  CircularProgress,
  FormControlLabel,
  Radio,
  RadioGroup,
  Typography,
} from '@mui/material';
import {
  VOICE_REACH_ADVERTISE,
  VOICE_REACH_FORWARD,
  voiceReachAdvertiseHelp,
  voiceReachCarrierSteps,
  voiceReachCarrierTitle,
  voiceReachForwardHelp,
  voiceReachForwardNote,
  voiceReachIntro,
  voiceReachKeepNumber,
  voiceReachPbxSteps,
  voiceReachPbxTitle,
  voiceReachPending,
} from '../utils/voiceNumberReachCopy';

function displayVoiceNumber(did) {
  const raw = (did || '').trim();
  const digits = raw.replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) {
    return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
  }
  if (digits.length === 10) {
    return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
  }
  return raw;
}

/**
 * TASK-849 — show the provisioned Twilio number and how the shop will use it.
 * Does not provision, advertise, or place the carrier forward.
 */
const VoiceNumberReachPanel = ({
  voiceDid,
  reach,
  hasPublicPhone,
  saving,
  error,
  onChange,
}) => {
  const ready = Boolean((voiceDid || '').trim());

  return (
    <Box
      id="voice-number-reach"
      sx={{
        mt: 1,
        mb: 2,
        p: 2,
        border: 1,
        borderColor: 'divider',
        borderRadius: 1,
      }}
    >
      <Typography variant="subtitle1" fontWeight="bold" gutterBottom>
        Your Voice number
      </Typography>
      {!ready ? (
        <Typography variant="body2">{voiceReachPending}</Typography>
      ) : (
        <>
          <Typography variant="h6" sx={{ mb: 1 }}>
            {displayVoiceNumber(voiceDid)}
          </Typography>
          {!hasPublicPhone ? (
            <Typography variant="body2">{voiceReachKeepNumber}</Typography>
          ) : (
            <>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {voiceReachIntro}
          </Typography>
          <RadioGroup
            value={reach || ''}
            onChange={(event) => onChange(event.target.value)}
          >
            <FormControlLabel
              value={VOICE_REACH_ADVERTISE}
              disabled={saving}
              control={<Radio size="small" />}
              label="I will advertise this number"
            />
            {reach === VOICE_REACH_ADVERTISE && (
              <Typography variant="body2" sx={{ pl: 4, pb: 1 }}>
                {voiceReachAdvertiseHelp}
              </Typography>
            )}
            <FormControlLabel
              value={VOICE_REACH_FORWARD}
              disabled={saving}
              control={<Radio size="small" />}
              label="I will forward my existing number to this number"
            />
            {reach === VOICE_REACH_FORWARD && (
              <Box sx={{ pl: 4, pb: 1 }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {voiceReachForwardHelp}
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {voiceReachCarrierTitle}
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2, mb: 1 }}>
                  {voiceReachCarrierSteps.map((step) => (
                    <Typography component="li" variant="body2" key={step}>
                      {step}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body2" fontWeight="bold">
                  {voiceReachPbxTitle}
                </Typography>
                <Box component="ol" sx={{ m: 0, pl: 2 }}>
                  {voiceReachPbxSteps.map((step) => (
                    <Typography component="li" variant="body2" key={step}>
                      {step}
                    </Typography>
                  ))}
                </Box>
                <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                  {voiceReachForwardNote}
                </Typography>
              </Box>
            )}
          </RadioGroup>
          {saving && (
            <Box display="flex" alignItems="center" gap={1} mt={1}>
              <CircularProgress size={16} />
              <Typography variant="caption">Saving</Typography>
            </Box>
          )}
            </>
          )}
        </>
      )}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}
    </Box>
  );
};

export default VoiceNumberReachPanel;
